import { slug, splitLines, unquote } from '../detect.js';
// Split a node token like `A[Hello]` or `A([DB])` into id+label+shape.
export const parseNodeToken = (token) => {
    const t = token.trim();
    if (!t)
        return null;
    // Handle `A:::className` class assignment first
    const classAssignment = /^([A-Za-z_][\w-]*):::(.+)$/.exec(t);
    if (classAssignment) {
        const [, id, className] = classAssignment;
        return { id: id, label: id, shape: 'rect', _className: className };
    }
    // Match leading id then optional shape brackets
    const idMatch = /^([A-Za-z_][\w-]*)/.exec(t);
    if (!idMatch)
        return null;
    const id = idMatch[1];
    const rest = t.slice(id.length);
    if (!rest)
        return { id, label: id };
    // Detect shape by surrounding brackets.
    // Order matters: longer/more-specific delimiters must be checked first.
    const bracketPairs = [
        { open: '[[', close: ']]', shape: 'rect' }, // subroutine -> rect
        { open: '((', close: '))', shape: 'ellipse' }, // circle
        { open: '([', close: '])', shape: 'stadium' }, // stadium / delay
        { open: '[(', close: ')]', shape: 'cylinder' }, // database
        { open: '{{', close: '}}', shape: 'hexagon' }, // hexagon
        { open: '[/', close: '/]', shape: 'parallelogram' },
        { open: '[\\', close: '\\]', shape: 'parallelogram' },
        { open: '[/', close: '\\]', shape: 'trapezoid' },
        { open: '[\\', close: '/]', shape: 'trapezoid' },
        { open: '|', close: '|', shape: 'parallelogram' }, // data input/output
        { open: '>', close: ']', shape: 'rect' }, // flag
        { open: '{', close: '}', shape: 'diamond' },
        { open: '[', close: ']', shape: 'rect' },
        { open: '(', close: ')', shape: 'stadium' }, // rounded rectangle
    ];
    for (const { open, close, shape } of bracketPairs) {
        if (rest.startsWith(open)) {
            // Find the matching close allowing for nested brackets inside.
            const closeIdx = findMatchingClose(rest, open, close);
            if (closeIdx >= 0 && closeIdx === rest.length - close.length) {
                const inner = rest.slice(open.length, closeIdx).trim();
                const label = inner || id;
                const trailing = rest.slice(closeIdx + close.length);
                const classMatch = /^:::([A-Za-z_][\w-]*)/.exec(trailing);
                return { id, label: unquote(label), shape, _className: classMatch?.[1] };
            }
        }
    }
    // Handle `id:::className` (no shape)
    const trailingClass = /:::(.+)$/.exec(rest);
    if (trailingClass)
        return { id, label: id, _className: trailingClass[1]?.trim() };
    return { id, label: id };
};
const findMatchingClose = (s, open, close) => {
    let depth = 0;
    for (let i = 0; i <= s.length - close.length; i++) {
        if (s.startsWith(open, i))
            depth++;
        else if (s.startsWith(close, i)) {
            depth--;
            if (depth === 0)
                return i;
        }
    }
    return -1;
};
const ARROW_REGEX = /(--+[ox]?|>+|=+>|-\.-+>|~+>|:>+|\.->|->)+/g;
const ARROW_STYLES = [
    { test: /==/, style: 'thick' },
    { test: /\.-/, style: 'dashed' },
    { test: /~/, style: 'dotted' },
    { test: /--/, style: 'solid' },
];
const classifyArrow = (raw) => {
    for (const { test, style } of ARROW_STYLES) {
        if (test.test(raw))
            return style;
    }
    return 'solid';
};
const LABEL_REGEX = /\|([^|]*)\|/;
const SUBGRAPH_RE = /^\s*subgraph\s+(\S+)?\s*(?:\[\s*["'`#]?(.+?)["'`#]?\s*\])?\s*$/;
const END_RE = /^\s*end\s*$/;
const INIT_RE = /^\s*(?:flowchart|graph)\s+(?:TB|TD|BT|RL|LR)?\s*$/i;
const DIRECTION_RE = /^\s*direction\s+(TB|TD|BT|RL|LR)\s*$/i;
const CLASSDEF_RE = /^\s*classDef\s+(\w+)\s+(.+)$/;
const CLASS_RE = /^\s*class\s+([\w,\s-]+)\s+(\w+)\s*$/;
const STYLE_RE = /^\s*style\s+(\w+)\s+(.+)$/;
export const parseFlowchart = (source) => {
    const nodes = new Map();
    const edges = [];
    const groups = [];
    const styles = {};
    const subgraphStack = [];
    const ensure = (decl) => {
        if (!nodes.has(decl.id)) {
            const classStyle = decl._className ? styles[decl._className] : undefined;
            nodes.set(decl.id, {
                id: decl.id,
                label: decl.label,
                shape: decl.shape ?? 'rect',
                parent: subgraphStack[subgraphStack.length - 1],
                style: classStyle ? { ...classStyle } : undefined,
            });
        }
        else if (decl.shape || decl._className) {
            const existing = nodes.get(decl.id);
            if (decl.shape && existing.shape === 'rect' && decl.shape !== 'rect')
                existing.shape = decl.shape;
            if (decl.label && decl.label !== decl.id)
                existing.label = decl.label;
            if (decl._className) {
                const classStyle = styles[decl._className];
                if (classStyle)
                    existing.style = { ...classStyle };
            }
        }
    };
    for (const raw of splitLines(source)) {
        const line = raw.trim();
        if (!line)
            continue;
        if (INIT_RE.test(line) || DIRECTION_RE.test(line))
            continue;
        const cls = CLASSDEF_RE.exec(line);
        if (cls) {
            const [, name, props] = cls;
            const style = {};
            for (const p of (props ?? '').split(',')) {
                const [k, v] = p.split(':').map((x) => x.trim());
                if (k && v)
                    style[k] = v.replace(/^["'`]+|["'`]+$/g, '');
            }
            styles[name] = style;
            continue;
        }
        const ca = CLASS_RE.exec(line);
        if (ca) {
            const [, ids, className] = ca;
            for (const id of (ids ?? '').split(',').map((s) => s.trim())) {
                const n = nodes.get(id);
                if (n)
                    n.style = { ...styles[className] };
            }
            continue;
        }
        const st = STYLE_RE.exec(line);
        if (st) {
            const [, id, props] = st;
            const style = {};
            for (const p of (props ?? '').split(',')) {
                const [k, v] = p.split(':').map((x) => x.trim());
                if (k && v)
                    style[k] = v.replace(/^["'`]+|["'`]+$/g, '');
            }
            const n = nodes.get(id);
            if (n)
                n.style = { ...style };
            else
                styles[id] = style;
            continue;
        }
        const sg = SUBGRAPH_RE.exec(line);
        if (sg) {
            const id = (sg[1] ?? `sg_${groups.length}`).trim();
            const label = (sg[2] ?? id).trim();
            const groupId = `sg_${slug(id)}`;
            subgraphStack.push(groupId);
            groups.push({
                id: groupId,
                label,
                parent: subgraphStack[subgraphStack.length - 2],
                members: [],
            });
            continue;
        }
        if (END_RE.test(line)) {
            subgraphStack.pop();
            continue;
        }
        // Tokenise the line by arrow operators.
        const tokens = line.split(ARROW_REGEX);
        if (tokens.length >= 3) {
            let prev = null;
            let pendingLabel;
            let pendingArrow;
            for (let i = 0; i < tokens.length; i++) {
                const token = tokens[i];
                if (i % 2 === 0) {
                    // Strip a leading |label| if present (the edge label for the preceding arrow).
                    let nodeText = token;
                    const labelMatch = /^\s*\|([^|]*)\|\s*/.exec(nodeText);
                    if (labelMatch) {
                        pendingLabel = labelMatch[1]?.trim();
                        nodeText = nodeText.slice(labelMatch[0].length);
                    }
                    const decl = parseNodeToken(nodeText);
                    if (!decl)
                        continue;
                    ensure(decl);
                    if (prev && pendingArrow) {
                        edges.push({
                            id: `e_${edges.length}_${prev.id}_${decl.id}`,
                            from: prev.id,
                            to: decl.id,
                            label: pendingLabel,
                            style: classifyArrow(pendingArrow),
                        });
                    }
                    prev = decl;
                    pendingLabel = undefined;
                    pendingArrow = undefined;
                }
                else {
                    pendingArrow = token;
                }
            }
        }
        else {
            // Bare node declaration.
            const decl = parseNodeToken(line);
            if (decl)
                ensure(decl);
        }
    }
    for (const n of nodes.values()) {
        if (n.parent) {
            const g = groups.find((g) => g.id === n.parent);
            if (g)
                g.members.push(n.id);
        }
    }
    return {
        type: 'flowchart',
        nodes: Array.from(nodes.values()),
        edges,
        groups,
        styles,
        meta: {},
    };
};
//# sourceMappingURL=flowchart.js.map